import { useInsurancePolicies } from '@/hooks/useInsurancePolicies';
import { useQueries } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PremiumPayment } from '@/types/insurance';
import { getUpcomingPayments } from '@/lib/insurance-utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, DollarSign, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PremiumPaymentForm, PremiumPaymentFormData } from './PremiumPaymentForm';
import { useCreatePremiumPayment } from '@/hooks/useCreatePremiumPayment';
import { toast } from 'sonner';

export function UpcomingPaymentsList() {
  const { data: policies, isLoading } = useInsurancePolicies();
  const createPayment = useCreatePremiumPayment();
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Fetch payments for all policies using useQueries (safe for dynamic lists)
  const policyIds = policies?.map(p => p.id) || [];
  const paymentResults = useQueries({
    queries: policyIds.map(id => ({
      queryKey: ['policy-payments', id],
      queryFn: async (): Promise<PremiumPayment[]> => {
        const { data, error } = await supabase
          .from('insurance_premium_payments')
          .select('*')
          .eq('policy_id', id)
          .order('payment_date', { ascending: false });
        if (error) throw error;
        return data as PremiumPayment[];
      },
      enabled: !!id,
    })),
  });

  // Build payments map
  const policyPayments: Record<string, any[]> = {};
  policyIds.forEach((id, i) => {
    policyPayments[id] = paymentResults[i]?.data || [];
  });

  const upcomingPayments = policies ? getUpcomingPayments(policies, policyPayments) : [];

  const handleRecordPayment = (policyId: string) => {
    setSelectedPolicyId(policyId);
    setIsDialogOpen(true);
  };

  const handleSubmitPayment = async (data: PremiumPaymentFormData) => {
    if (!selectedPolicyId) return;

    try {
      await createPayment.mutateAsync({
        data: {
          policy_id: selectedPolicyId,
          amount: data.amount,
          currency: selectedPolicy!.currency,
          payment_date: data.payment_date,
          notes: data.notes,
        },
        policy: selectedPolicy!,
      });
      toast.success('Payment recorded successfully');
      setIsDialogOpen(false);
      setSelectedPolicyId(null);
    } catch (error) {
      toast.error('Failed to record payment');
      console.error(error);
    }
  };

  const selectedPolicy = policies?.find(p => p.id === selectedPolicyId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Payments</CardTitle>
          <CardDescription>Loading upcoming payments...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (upcomingPayments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Payments</CardTitle>
          <CardDescription>No payments due in the next 7 days</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Payments</CardTitle>
          <CardDescription>
            {upcomingPayments.length} payment{upcomingPayments.length !== 1 ? 's' : ''} due within 7 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {upcomingPayments.map((payment) => (
              <div
                key={payment.policyId}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {payment.policyType}
                    </Badge>
                    <span className="text-sm font-medium">{payment.providerName}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(payment.dueDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      <span>
                        {payment.amount.toFixed(2)} {payment.currency}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <AlertCircle className="h-3 w-3 text-orange-500" />
                    <span className="text-orange-500">
                      Due in {payment.daysUntilDue} day{payment.daysUntilDue !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRecordPayment(payment.policyId)}
                >
                  Record Payment
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Premium Payment</DialogTitle>
            <DialogDescription>
              Record a payment for this insurance policy
            </DialogDescription>
          </DialogHeader>
          {selectedPolicy && (
            <PremiumPaymentForm
              policy={selectedPolicy}
              onSubmit={handleSubmitPayment}
              isLoading={createPayment.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
